import { useState, type FormEvent } from 'react';
import { Plus, Pencil, Trash2, Settings, Globe, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import FormattedDate from '@/components/common/FormattedDate';
import {
  useWorkflowTypeDefaults,
  useWorkflowTypes,
  useCreateWorkflowTypeDefault,
  useUpdateWorkflowTypeDefault,
  useDeleteWorkflowTypeDefault,
} from '@/hooks/useWorkflows';
import type { WorkflowTypeDefault, WorkflowEnvironment } from '@/lib/types';

const envBadgeStyles: Record<WorkflowEnvironment, string> = {
  production: 'bg-green-500/20 text-green-400 border-green-500/30',
  staging: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
};

// A type+environment can have one global default plus per-customer overrides, so
// row identity in action labels and confirmations needs the scope to stay unambiguous.
const rowLabel = (def: WorkflowTypeDefault): string =>
  `${def.customer_id ? `customer ${def.customer_id}` : 'global'} defaults for ${def.type_name} (${def.environment})`;

export default function WorkflowTypeDefaultsTable() {
  const [showCreate, setShowCreate] = useState(false);
  const [editingDefault, setEditingDefault] = useState<WorkflowTypeDefault | null>(null);

  // Form state
  const [typeName, setTypeName] = useState('');
  const [environment, setEnvironment] = useState<WorkflowEnvironment>('production');
  const [defaultSubtype, setDefaultSubtype] = useState('');
  const [defaultVersion, setDefaultVersion] = useState('1.0');

  const { data: defaultsData, isLoading } = useWorkflowTypeDefaults();
  const { data: typesData } = useWorkflowTypes();
  const createDefault = useCreateWorkflowTypeDefault();
  const updateDefault = useUpdateWorkflowTypeDefault();
  const deleteDefault = useDeleteWorkflowTypeDefault();

  const defaults = defaultsData?.items || [];
  const types = typesData?.items || [];

  const resetForm = () => {
    setTypeName('');
    setEnvironment('production');
    setDefaultSubtype('');
    setDefaultVersion('1.0');
  };

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await createDefault.mutateAsync({
        type_name: typeName,
        environment,
        default_subtype: defaultSubtype,
        default_version: defaultVersion,
      });
      toast.success('Default created');
      setShowCreate(false);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create default');
    }
  };

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingDefault) return;

    try {
      await updateDefault.mutateAsync({
        id: editingDefault.id,
        payload: {
          default_subtype: defaultSubtype,
          default_version: defaultVersion,
        },
      });
      toast.success('Default updated');
      setEditingDefault(null);
      resetForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update default');
    }
  };

  const handleDelete = async (def: WorkflowTypeDefault) => {
    if (!confirm(`Delete ${rowLabel(def)}?`)) {
      return;
    }

    try {
      await deleteDefault.mutateAsync(def.id);
      toast.success('Default deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete default');
    }
  };

  const startEdit = (def: WorkflowTypeDefault) => {
    setEditingDefault(def);
    setTypeName(def.type_name);
    setEnvironment(def.environment);
    setDefaultSubtype(def.default_subtype);
    setDefaultVersion(def.default_version);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info */}
      <div className="p-4 rounded-lg bg-muted/30 border border-border">
        <p className="text-sm text-muted-foreground">
          Type defaults define which subtype and version to use when a job request doesn't specify them.
          Each type+environment combination can have one global default and optionally customer-specific overrides.
        </p>
      </div>

      {/* Create Form */}
      {showCreate ? (
        <form onSubmit={handleCreate} className="p-4 rounded-lg bg-muted/30 border border-border space-y-3">
          <h3 className="text-sm font-medium">Create Type Default</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="create-type" className="text-xs font-medium">Workflow Type</label>
              <select
                id="create-type"
                value={typeName}
                onChange={(e) => setTypeName(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              >
                <option value="">Select type...</option>
                {types.map((type) => (
                  <option key={type.name} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="create-env" className="text-xs font-medium">Environment</label>
              <select
                id="create-env"
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as WorkflowEnvironment)}
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="production">Production</option>
                <option value="staging">Staging</option>
                <option value="development">Development</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="create-subtype" className="text-xs font-medium">Default Subtype</label>
              <input
                id="create-subtype"
                type="text"
                value={defaultSubtype}
                onChange={(e) => setDefaultSubtype(e.target.value)}
                placeholder="e.g., flux, sdxl"
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>
            <div>
              <label htmlFor="create-version" className="text-xs font-medium">Default Version</label>
              <input
                id="create-version"
                type="text"
                value={defaultVersion}
                onChange={(e) => setDefaultVersion(e.target.value)}
                placeholder="e.g., 1.0"
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                resetForm();
              }}
              className="px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createDefault.isPending}
              className="px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createDefault.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm"
        >
          <Plus className="h-4 w-4" />
          Add Type Default
        </button>
      )}

      {/* Edit Form */}
      {editingDefault && (
        <form onSubmit={handleUpdate} className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30 space-y-3">
          <h3 className="text-sm font-medium">
            Edit: {editingDefault.type_name} / {editingDefault.environment}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-subtype" className="text-xs font-medium">Default Subtype</label>
              <input
                id="edit-subtype"
                type="text"
                value={defaultSubtype}
                onChange={(e) => setDefaultSubtype(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>
            <div>
              <label htmlFor="edit-version" className="text-xs font-medium">Default Version</label>
              <input
                id="edit-version"
                type="text"
                value={defaultVersion}
                onChange={(e) => setDefaultVersion(e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
                required
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditingDefault(null);
                resetForm();
              }}
              className="px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateDefault.isPending}
              className="px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-50"
            >
              {updateDefault.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {/* Defaults List */}
      {defaults.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Settings className="h-12 w-12 mb-4 opacity-50" />
          <p>No type defaults configured</p>
          <p className="text-sm mt-1">Add defaults to enable automatic template resolution</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Environment</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Scope</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Default Subtype</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Default Version</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Created</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {defaults.map((def) => (
                <tr key={def.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm">{def.type_name}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={cn(
                        'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
                        envBadgeStyles[def.environment]
                      )}
                    >
                      {def.environment}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {def.customer_id ? (
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <User className="h-3 w-3" />
                        Customer
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        <Globe className="h-3 w-3" />
                        Global
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm">{def.default_subtype}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-muted-foreground">{def.default_version}</span>
                  </td>
                  <td className="py-3 px-4">
                    <FormattedDate date={def.create_ts} />
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => startEdit(def)}
                        className="p-1.5 rounded-md hover:bg-muted transition-colors"
                        title="Edit"
                        aria-label={`Edit ${rowLabel(def)}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(def)}
                        className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400 transition-colors"
                        title="Delete"
                        aria-label={`Delete ${rowLabel(def)}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
